import { Form, Formik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { BsCheckLg } from 'react-icons/bs';
import { FaPencilAlt } from 'react-icons/fa';
import { Col, Label, Row, UncontrolledTooltip } from 'reactstrap';
import { object, string } from 'yup';
import {
  createAccountNumber,
  editAccountNumber,
  getAccountNumber,
  getEntityList,
  getSegmentValue,
  getTotalAccountNumber,
} from '../../../api/accountNumber';
import { getSegmentDefinition } from '../../../api/segmentDefinition';
import { Button, Layout, Pagination, Select, Table } from '../../../common/components';
import { useSetLoading } from '../../../common/hooks/appearance';
import { usePagination } from '../../../common/hooks/data';
import { ACTION_EDIT, ACTION_NEW } from '../../../constants/gl-configuration';
import { accountNumberTableColumn } from '../../../utils/constant';
import { apiErrorHandler } from '../../../utils/handlers';
import { asyncDebounce } from '../../../utils/utils';
import AccountNumberModal from './accountNumberModal';
import CreatedSuccessModal from './CreatedSuccessModal';

const AccountNumber = () => {
  const { page, limit, setPage, setLimit } = usePagination();
  const [loading, setLoading] = useState(false);
  const [segment, setSegment] = useState([]);
  const [segmentDropdown, setSegmentDropdown] = useState([]);
  const [accountNumberPayload, setaccountNumberPayload] = useState({
    searchValue: '',
    accountNumberSearch: [],
  });
  const [accountNumberDropdown, setAccountNumberDropdown] = useState([]);
  const [accountNumberList, setAccountNumberList] = useState([]);
  const [totalAccountNumbers, setTotalAccountNumbers] = useState(0);
  const [message, setMessage] = useState('');
  const [entityList, setEntityList] = useState([]);
  const [isNewAccountNumberModalOpen, setIsNewAccountNumberModalOpen] = useState(false);
  const [createAccount, setCreateAccount] = useState(null);
  const [editableValue, setEditableValue] = useState(null);
  const [isEditAccountNumberModalOpen, setIsEditAccountNumberModalOpen] = useState(false);
  const [isCreatedAccountModalOpen, setIsCreatedAccountModalOpen] = useState(false);
  const [isEditedAccountModalOpen, setIsEditedAccountModalOpen] = useState(false);

  const toggleNewAccountNumberModal = () => {
    setIsNewAccountNumberModalOpen(!isNewAccountNumberModalOpen);
  };

  const toggleEditAccountNumberModal = () => {
    setIsEditAccountNumberModalOpen(!isEditAccountNumberModalOpen);
  };

  const toggleCreatedAccountModal = () => {
    setIsCreatedAccountModalOpen(!isCreatedAccountModalOpen);
  };

  const toggleEditedAccountModal = () => {
    setIsEditedAccountModalOpen(!isEditedAccountModalOpen);
  };

  const createAccountNumberClick = async (payload) => {
    setLoading(true);
    try {
      setCreateAccount(payload);
      await createAccountNumber(payload);
      setLoading(false);
      toggleCreatedAccountModal();
      toggleNewAccountNumberModal();
      fetchAccountNumbers(false, true);
    } catch (err) {
      setLoading(false);
      apiErrorHandler(err);
    }
  };

  const editAccountNumberClick = async (payload) => {
    setLoading(true);
    try {
      await editAccountNumber(payload);
      setEditableValue({ ...editableValue, ...payload });
      setLoading(false);
      toggleEditedAccountModal();
      toggleEditAccountNumberModal();
      fetchAccountNumbers(false, true);
    } catch (err) {
      setLoading(false);
      apiErrorHandler(err);
    }
  };

  const fetchSegmentDefinition = async (searchVal) => {
    setLoading(true);
    try {
      const params = {
        searchValue: searchVal || '',
      };
      const response = await getSegmentDefinition(params);
      const formatedResp = response.data.data.map((d) => ({
        value: d.accountSegmentId,
        label: d.title,
      }));
      setLoading(false);
      setSegment(formatedResp);
    } catch (err) {
      setLoading(false);
      apiErrorHandler(err);
    }
  };

  const fetchAccountNumbers = async (
    isOptionUpdated = false,
    shouldListUpdate = false,
    payload,
  ) => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
        ...accountNumberPayload,
        ...payload,
      };
      if (payload?.page) {
        setPage(payload?.page);
      }
      const response = await getAccountNumber(params);
      if (shouldListUpdate) {
        setAccountNumberList(response?.data?.data);
      }
      if (isOptionUpdated) {
        const countParams = {
          ...accountNumberPayload,
          ...payload,
        };
        // Deleting Key Page and Limit for Count Api If Exists
        delete countParams.page;
        delete countParams.limit;
        const count = await getTotalAccountNumber(countParams);
        if (count?.data?.data?.totalCount !== 0) {
          setTotalAccountNumbers(count?.data?.data?.totalCount);
        } else {
          setMessage('No Account Numbers Available');
        }
      }
      setLoading(false);
      const formatedResp = response?.data?.data?.map((d) => ({
        value: d.accountNumber,
        label: d.description ? `${d.accountNumber} - ${d.description}` : d.accountNumber,
      }));
      setAccountNumberDropdown(formatedResp);
      return formatedResp;
    } catch (err) {
      setLoading(false);
      apiErrorHandler(err);
    }
  };

  const debouncefetchAccountNumbers = asyncDebounce(fetchAccountNumbers, 1000);

  const fetchEntityList = async () => {
    setLoading(true);
    try {
      const response = await getEntityList();
      setEntityList(response?.data?.data);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      apiErrorHandler(err);
    }
  };

  const fetchSegmentValue = async (segmentId, searchVal) => {
    const params = {
      accountSegmentId: segmentId,
      searchValue: searchVal || '',
    };
    setLoading(true);
    try {
      const response = await getSegmentValue(params);
      setLoading(false);
      const formatedResp = response?.data?.data?.map((d) => ({
        value: d.segmentValue,
        label: d.description ? `${d.segmentValue} - ${d.description}` : d.segmentValue,
      }));
      return formatedResp;
    } catch (err) {
      setLoading(false);
      apiErrorHandler(err);
    }
  };

  const debouncefetchSegmentValue = asyncDebounce(fetchSegmentValue, 1000);

  const fetchAllSegmentValue = async () => {
    const res = await Promise.all(
      segment?.map((item) => {
        return fetchSegmentValue(item?.value, '');
      }),
    );
    setSegmentDropdown(res);
  };

  useEffect(() => {
    fetchSegmentDefinition();
    fetchAccountNumbers();
    fetchEntityList();
  }, []);

  useEffect(() => {
    fetchAllSegmentValue();
  }, [segment]);

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  return (
    <Layout>
      <div className="atd-div">
        <div className="atd-block mt-4">
          <Formik
            initialValues={{
              an: segment?.map(() => null),
              accountNumber: null,
            }}
            validationSchema={object({
              accountNumber: string().trim().required(`Account Number is Required`),
            })}
            enableReinitialize
          >
            {({ values, setFieldValue }) => {
              return (
                <Form>
                  <h4 className="atd-block-heading">Account Number</h4>
                  <Row>
                    <Col lg="12" className="account-number-input">
                      <Label>Account Number</Label>
                      <div className="common-local-search w-100">
                        <i className="fas fa-search" />
                        <Select
                          formInput
                          value={values?.accountNumber}
                          onChange={(val) => {
                            setFieldValue('accountNumber', val);
                          }}
                          name="accountNumber"
                          classNamePrefix="select-dropdown"
                          size="lg"
                          isClearable
                          cacheOptions
                          isAsync
                          loadOptions={(v) =>
                            debouncefetchAccountNumbers(false, false, {
                              searchValue: v,
                              accountNumberSearch: [],
                            })
                          }
                          defaultOptions={accountNumberDropdown}
                          required
                          disabled={values?.an?.toString() !== Array(values?.an?.length).join(',')}
                        />
                      </div>
                    </Col>
                  </Row>
                  <Row className="mb-5">
                    {segment?.map((item, index) => (
                      <Col lg="6" className="account-number-input" key={item?.value}>
                        <Label>
                          Segment {index + 1} - {item?.label}
                        </Label>
                        <div className="common-local-search w-100">
                          <i className="fas fa-search" />
                          <Select
                            formInput
                            name="an"
                            classNamePrefix="select-dropdown"
                            size="lg"
                            isClearable
                            cacheOptions
                            isAsync
                            value={values.an[index]}
                            onChange={(val) => {
                              const temp = [...values.an];
                              temp[index] = val;
                              setFieldValue('accountNumber', null);
                              setFieldValue('an', temp);
                            }}
                            loadOptions={(e) => debouncefetchSegmentValue(item?.value, e)}
                            defaultOptions={segmentDropdown[index]}
                            required
                            disabled={!!values?.accountNumber}
                          />
                        </div>
                      </Col>
                    ))}
                  </Row>
                  <hr />
                  <div className="atd-btn-section account-number-btn">
                    <Button
                      color="primary"
                      className="atd-btn"
                      onClick={() => {
                        const payload = {
                          ...(values?.accountNumber
                            ? {
                                searchValue: values?.accountNumber?.value,
                                accountNumberSearch: [],
                                page: 1,
                              }
                            : {
                                page: 1,
                                searchValue: '',
                                accountNumberSearch: values?.an
                                  ?.map((subItem) => {
                                    return {
                                      searchValue: subItem?.value || '',
                                    };
                                  })
                                  .every((item) => item.searchValue === '')
                                  ? []
                                  : values?.an?.map((subItem) => {
                                      return {
                                        searchValue: subItem?.value || '',
                                      };
                                    }),
                              }),
                        };
                        setaccountNumberPayload({ ...payload });
                        fetchAccountNumbers(true, true, payload);
                      }}
                    >
                      Search
                    </Button>
                    <Button
                      color="primary"
                      className="atd-btn"
                      onClick={toggleNewAccountNumberModal}
                    >
                      Create New Account
                    </Button>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
        {accountNumberList?.length > 0 ? (
          <div>
            <h4 className="atd-heading">Account Number Details</h4>
            <Table
              headers={accountNumberTableColumn}
              bordered
              responsive
              className="dashboard-table"
            >
              <tbody>
                {accountNumberList?.map((item, index) => (
                  <tr key={item?.accountNumberId} className="table-body-tr">
                    <td>
                      <FaPencilAlt
                        onClick={() => {
                          setEditableValue(item);
                          toggleEditAccountNumberModal();
                        }}
                        style={{ cursor: 'pointer' }}
                      />
                    </td>
                    <td>{item?.accountNumber}</td>
                    <td id={`description-${index}`}>
                      {item?.description.slice(0, 30)}
                      <UncontrolledTooltip target={`description-${index}`} placement="bottom">
                        {item?.description}
                      </UncontrolledTooltip>
                    </td>
                    {entityList.map((subItem) => (
                      <td className="text-center" key={subItem?.accountEntityId}>
                        {item?.accountNumberTemplate?.map((microItem) =>
                          microItem?.templateBusinessId === subItem?.accountEntityId ? (
                            <BsCheckLg key={`BsCheckLg-${subItem?.accountEntityId}`} />
                          ) : null,
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
            <div className="pagination-araging-report">
              <Pagination
                totalResults={totalAccountNumbers}
                page={page}
                limit={limit}
                setPage={(pageNo) => {
                  setPage(pageNo);
                  debouncefetchAccountNumbers(false, true, { page: pageNo });
                }}
                setLimit={(rowlimit) => {
                  setLimit(rowlimit);
                  setPage(1);
                  debouncefetchAccountNumbers(false, true, {
                    limit: rowlimit,
                    page: 1,
                  });
                }}
              />
            </div>
          </div>
        ) : (
          <div className="no-records">{message}</div>
        )}
      </div>
      {isNewAccountNumberModalOpen && (
        <AccountNumberModal
          isOpen={isNewAccountNumberModalOpen}
          toggle={toggleNewAccountNumberModal}
          segmentList={segment}
          entityList={entityList}
          segmentDropdownValues={segmentDropdown}
          onSubmit={createAccountNumberClick}
          fetchSegmentValue={fetchSegmentValue}
          action={ACTION_NEW}
        />
      )}
      {isEditAccountNumberModalOpen && (
        <AccountNumberModal
          isOpen={isEditAccountNumberModalOpen}
          toggle={toggleEditAccountNumberModal}
          segmentList={segment}
          entityList={entityList}
          onSubmit={editAccountNumberClick}
          selectedValue={editableValue}
          action={ACTION_EDIT}
        />
      )}
      <CreatedSuccessModal
        isOpen={isCreatedAccountModalOpen}
        toggle={toggleCreatedAccountModal}
        action={ACTION_NEW}
        selectedValue={createAccount}
      />
      <CreatedSuccessModal
        isOpen={isEditedAccountModalOpen}
        toggle={toggleEditedAccountModal}
        action={ACTION_EDIT}
        selectedValue={editableValue}
      />
    </Layout>
  );
};

export default AccountNumber;
